import { ColumnDef } from '@tanstack/react-table'
import { camelCase, difference, isEmpty, startCase } from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import Combo from 'app/components/ui/combo/combo'
import { ImportWizardContext } from '../ImportWizard'
import Spinner from '../Spinner'
import SimpleTable from '../table/SimpleTable'
import { showModal } from 'app/lib/modal'
import { useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const getActivitiesFileHeaders = (orgId?: string) => {
  let isSMC = isSMCOrg(orgId)
  let baseHeaders = [
    `Workspace`,
    `Location`,
    `Vendor`,
    `Activity Type`,
    `Name`,
    `Activity Date`,
    `Requested Appointment Date`,
    `Phone`,
    `Email`,
    `Scheduled`,
    `Requested Treatment`,
    `Contact Owner`,
  ]

  if (isSMC) {
    return [...baseHeaders, `Channel`]
  }
  return [...baseHeaders, `Source`, `Medium`, `Campaign`]
}

export default function Step2() {
  const user = useUser()
  const { csvFile, columnMapping, dataToImport, openErrorModal, setColumnMapping, setErrors, setOpenErrorModal } =
    useContext(ImportWizardContext)
  let [isLoading, setIsLoading] = useState<boolean>(isEmpty(columnMapping))
  let [missingColumns, setMissingColumns] = useState<string[]>([])

  let errorColumns = useMemo(
    () => [
      {
        id: `missingColumns`,
        header: `Missing columns`,
        accessorFn: (row) => startCase(row),
        meta: {
          defaultVisible: true,
        },
      },
    ],
    [missingColumns],
  )

  useEffect(() => {
    if (!user || dataToImport === undefined || !isEmpty(columnMapping)) return
    let fileHeaders = Object.keys(dataToImport![0])

    let headers = getActivitiesFileHeaders(user.orgId)
    if (user.workspaceId) {
      headers = headers.filter((header) => header !== `Workspace`)
    }

    let missingColumns = difference(
      headers?.map((header) => camelCase(header)),
      fileHeaders?.map((header) => {
        if (header === `leadOwner`) return `contactOwner`
        return header
      }),
    )
    setMissingColumns(missingColumns)
    let tableData: Record<string, string>[] = columnMapping ?? []
    fileHeaders.forEach((element) => {
      // Map CSV columns to the correct field names
      let mapping = element
      if (element === `source`) mapping = `utmSource`
      if (element === `medium`) mapping = `utmMedium`
      if (element === `campaign`) mapping = `utmCampaign`
      if (element === `leadOwner`) mapping = `contactOwner`

      tableData = [
        ...tableData,
        {
          columnName: element,
          mapping: mapping,
          example: dataToImport[0][element],
        },
      ]
    })
    debug(`imports`, `step 2`, `tableData`, tableData)
    setColumnMapping(tableData)
    setIsLoading(false)
    setErrors(missingColumns.length)
  }, [csvFile, user])

  useEffect(() => {
    if (openErrorModal)
      showModal(<SimpleTable columns={errorColumns} data={missingColumns} hideFooter />, {
        title: `Missing columns`,
        isFullScreen: false,
        is80Percent: true,
        onClose: () => setOpenErrorModal(false),
        useRadix: true,
      })
  }, [openErrorModal])

  if (!user) return null

  let tableData: Record<string, string>[] = columnMapping ?? []

  // Get the appropriate headers based on organization
  let headers = getActivitiesFileHeaders(user.orgId)

  let mappingColumnOptions: OptionType[] = headers?.map(
    (fileHeader) => ({ name: fileHeader, value: camelCase(fileHeader), id: fileHeader }) as OptionType,
  )

  let handleMappingChange = (option: OptionType, index: any) => {
    let mapping: { [key: string]: string }[] = [...columnMapping]
    debug(`imports`, `current column mapping`, mapping)
    let changedIndex = mapping.findIndex((map) => map?.mapping === option.value)
    let currentMapping = mapping[index]
    let previousMapping = mapping[changedIndex]
    mapping.splice(index, 1, {
      ...currentMapping,
      mapping: option.value,
    })
    mapping.splice(changedIndex, 1, {
      ...previousMapping,
      mapping: currentMapping?.mapping,
    })
    debug(`imports`, `updated column mapping`, mapping)
    setColumnMapping(mapping)
  }

  let fieldsMappingColumns: ColumnDef<any, any>[] = [
    {
      id: `spreadsheetColumn`,
      header: `Spreadsheet Columns`,
      accessorFn: (row) => row.columnName,
      cell: (props) => <div className="capitalize">{startCase(props.getValue())}</div>,
      meta: {
        defaultVisible: true,
      },
    },
    {
      id: `fieldMapping`,
      header: `Field Mapping`,
      accessorFn: (row) => row?.mapping,
      meta: {
        defaultVisible: true,
        tooltipClassName: `overflow-visible`,
        ControlledComponent: (row, col, index) => (
          <Combo
            inputClassName="!h-full !border-0 !shadow-none !p-2 text-body-xs text-default"
            options={mappingColumnOptions}
            value={{ name: startCase(row?.mapping), value: row?.mapping, id: row?.mapping }}
            onChange={(option) => handleMappingChange(option, index)}
          />
        ),
      },
    },
    {
      id: `example`,
      header: `Example`,
      accessorFn: (row) => row.example,
      meta: {
        defaultVisible: true,
      },
    },
  ]

  return (
    <>
      <h5 className="mb-4 text-strong">Fields mapping</h5>
      {isLoading ? (
        <div className="flex justify-center">
          <Spinner size={20} />
        </div>
      ) : (
        <div className="flex h-full w-full grow flex-col overflow-hidden">
          <SimpleTable columns={fieldsMappingColumns} data={tableData} hideFooter />
        </div>
      )}
    </>
  )
}
